/**
 * Lectures Event Cell - Frontend
 * @description Cell component for displaying event in LecturesTable
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CellContext } from '@tanstack/react-table';
import { icons, LucideIcon } from 'lucide-react';
import { LecturesTableData } from '@/types/operational-ui.types';
import { getEventStyles } from '@/utils/operational-ui.utils';
import { useEventTypes } from '@/hooks/useEventTypes';

interface LecturesEventCellProps {
  info: CellContext<LecturesTableData, string>;
}

export const LecturesEventCell: React.FC<LecturesEventCellProps> = ({ info }) => {
  const { t, i18n } = useTranslation();
  const event = info.getValue();
  const { eventTypesByCode } = useEventTypes();
  const styles = getEventStyles(event);
  const configured = eventTypesByCode.get(String(event).toUpperCase()) ?? eventTypesByCode.get(event);
  // Prefer live catalog icon; getEventStyles may be stale until dynamic map is refreshed.
  const IconComponent =
    (configured?.icon
      ? (icons[configured.icon as keyof typeof icons] as LucideIcon | undefined)
      : undefined) || styles.icon;
  const configuredIconColor = configured?.iconColor;
  const label = configured
    ? i18n.language?.startsWith('es')
      ? configured.labelEs
      : configured.labelEn
    : t(`pages.lectures.events.${event}`, event);

  return (
    <div className="flex items-center gap-2 ml-5">
      <IconComponent className="h-4 w-4" style={{ color: configuredIconColor ?? styles.color }} />
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${configured ? '' : `${styles.bg} ${styles.text}`}`}
        style={
          configured
            ? {
                backgroundColor: configured.bgColor,
                color: configured.textColor,
                border: '1px solid rgba(0,0,0,0.08)',
              }
            : undefined
        }
      >
        {label}
      </span>
    </div>
  );
};
